import { useCallback, useMemo, useState } from "react";

/**
 * Options for configuring the filter synchronization hook.
 * @template T The type of the filter state object.
 */
export interface FilterSyncOptions<T> {
  /** Initial state of the filters */
  initialState: T;
  /** Key used for storing filters in localStorage */
  storageKey: string;
  /** Function to serialize the filter state for storage (default: JSON.stringify) */
  serialize?: (state: T) => string;
  /** Function to deserialize the stored filter state (default: JSON.parse) */
  deserialize?: (serialized: string) => T;
  /** Key used for off filters from localStorage */
  offStorageKey?: boolean;
}

/** Represents a sort item with an id and sort direction */
export interface SortItem {
  id: string;
  desc: boolean;
}

/** Possible types for filter values */
export type FilterValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | unknown[]
  | Date
  | SortItem[]
  | FilterValue[];
export function useFilterSync<T extends Record<string, FilterValue>>({
                                                                       initialState,
                                                                       storageKey,
                                                                       serialize = JSON.stringify,
                                                                       deserialize = JSON.parse,
                                                                       offStorageKey = false,
                                                                     }: FilterSyncOptions<T>) {
  // Fetch initial filters from localStorage, merging with initialState
  const initialFilters = useMemo(() => {
    const storedFilters = offStorageKey ? "" : localStorage.getItem(storageKey);
    if (storedFilters) {
      return { ...initialState, ...deserialize(storedFilters) };
    }
    return initialState;
  }, [offStorageKey, storageKey, deserialize, initialState]);

  const [filters, setFilters] = useState<T>(initialFilters);

  // Function to update filters and store them in localStorage
  const updateFilters = useCallback(
    (newFilters: Partial<T>) => {
      setFilters((prevFilters) => {
        const updatedFilters = { ...prevFilters, ...newFilters };
        localStorage.setItem(storageKey, serialize(updatedFilters));
        return updatedFilters;
      });
    },
    [storageKey, serialize],
  );

  // Function to clear filters and remove them from localStorage
  const clearFilters = useCallback(() => {
    setFilters(initialState);
    localStorage.removeItem(storageKey);
  }, [initialState, storageKey]);

  return { filters, updateFilters, clearFilters };
}
